// Imports for external libraries go here.
import React, { FC, useId } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { HotelCardProps } from './HotelCard.types';
import { StyledHotelCard } from './HotelCard.styles';
import { Link, Image, LogoContainer } from '@marriott/mi-ui-library';
import { IMAGE_URL_DOMAIN } from '../../modules/constants/constant';
import { useViewport } from '../../modules/helpers';

// Use named rather than default exports.
export const HotelCard: FC<HotelCardProps> = ({ props, currentLocale, variation, showReviews, ctaLabel }) => {
  // Generate a unique ID for the component if needed, rather than using a fixed ID.
  const uniqueId = useId();

  const getCardLink = () => {
    if (props?.node?.seoNickname) {
      return `${currentLocale}/${props?.node?.seoNickname}.mi`;
    }
    return '';
  };

  const viewPort = useViewport();

  // Get dynamic image URL based on prop structure
  const getImageUrl = () => {
    if (viewPort === 'Mobile' || variation === 'list-view') {
      return IMAGE_URL_DOMAIN + props?.node?.media?.primaryImage?.edges[0]?.node?.imageUrls?.square || '';
    } else {
      return IMAGE_URL_DOMAIN + props?.node?.media?.primaryImage?.edges[0]?.node?.imageUrls?.wideHorizontal || '';
    }
  };

  const getCardContent = () => {
    // Get hotel name and image details from props
    const hotelName = props?.node?.basicInformation?.name;
    const imageUrl = getImageUrl();
    const hotelReviews = props?.node?.reviews;

    if (variation === 'list-view') {
      return (
        <div className="d-flex flex-row list-mini-card-container pb-3 color-scheme1">
          <Image
            title={hotelName}
            defaultImageURL={imageUrl}
            altText={hotelName}
            customClass="list-mini-card-image"
            loading={'eager'}
          />
          <div className="d-flex flex-column justify-content-between ml-3 list-mini-card-details pb-2">
            <div className="t-subtitle-m">{hotelName}</div>
            {showReviews && (
              <div className="review-container t-font-xs">
                <span className="icon-star-fill mr-1"></span>
                <span className="mr-1">{hotelReviews?.stars?.count}</span>
                <span className="">{`${hotelReviews?.numberOfReviews?.count} Reviews`}</span>
              </div>
            )}
            <Link
              linkHref={getCardLink()}
              target={props?.openInaNewTab ? '_blank' : '_self'}
              linkClassName="m-link-tertiary-button"
              id={`mini-card-container-${uniqueId}`}
            >
              {ctaLabel}
            </Link>
          </div>
        </div>
      );
    } else {
      if (viewPort === 'Mobile') {
        return (
          <div className="d-flex flex-row mobile-mini-card-container p-2 color-scheme1">
            <Image
              title={hotelName}
              defaultImageURL={imageUrl}
              altText={hotelName}
              customClass="mobile-mini-card-image"
              loading={'eager'}
            />
            <div className="d-flex flex-column justify-content-between ml-2 mobile-mini-card-details pb-2">
              <div className="t-subtitle-m">{hotelName}</div>
              {showReviews && (
                <div className="review-container t-font-xs">
                  <span className="icon-star-fill mr-1"></span>
                  <span className="mr-1">{hotelReviews?.stars?.count}</span>
                  <span className="">{`${hotelReviews?.numberOfReviews?.count} Reviews`}</span>
                </div>
              )}
              <Link
                linkHref={getCardLink()}
                target={props?.openInaNewTab ? '_blank' : '_self'}
                linkClassName="m-link-tertiary-button"
                id={`mini-card-container-${uniqueId}`}
              >
                {ctaLabel}
              </Link>
            </div>
          </div>
        );
      } else {
        return (
          <div className="d-flex flex-column mini-card-container p-2 color-scheme1">
            <div className="mini-card-img-container">
              <Image
                title={hotelName}
                defaultImageURL={imageUrl}
                altText={hotelName}
                customClass="mini-card-image"
                loading={'eager'}
              />
              <div className="brand-logo-container color-scheme2">
                <LogoContainer
                  brand={props?.node?.basicInformation?.brand?.id}
                  aspectRatio={'3:2'}
                  width={90}
                  height={60}
                  alignment={'Bottom Left'}
                  isLink={false}
                  color={'background-aware'}
                  ariaRole="img"
                />
              </div>
            </div>
            <div className="d-flex flex-column justify-content-between mini-card-details pb-2 pl-1">
              <div className="pt-3 pb-2 t-subtitle-m">{hotelName}</div>
              {showReviews && (
                <div className="review-container t-font-xs">
                  <span className="icon-star-fill mr-1"></span>
                  <span className="mr-1">{hotelReviews?.stars?.count}</span>
                  <span className="">{`${hotelReviews?.numberOfReviews?.count} Reviews`}</span>
                </div>
              )}
              <Link
                linkHref={getCardLink()}
                target={props?.openInaNewTab ? '_blank' : '_self'}
                linkClassName="m-link-tertiary-button"
                id={`mini-card-container-${uniqueId}`}
              >
                {ctaLabel}
              </Link>
            </div>
          </div>
        );
      }
    }
  };

  return (
    <StyledHotelCard data-testid="luxury-HotelCard" data-component-name="m-luxury-HotelCard">
      <div>{getCardContent()}</div>
    </StyledHotelCard>
  );
};
